import ComponentName from '../constant/ComponentName';
import { DeployableAccessBase } from '../domain/access/BaseAccess';

export interface BulkActionVisibility {
  showDeploy: boolean;
  showUndeploy: boolean;
  showRemove: boolean;
}

export interface SelectedItemsStatus {
  hasDraft: boolean;
  hasActive: boolean;
  hasMixed: boolean;
  draftCount: number;
  activeCount: number;
}

const DEPENDENCY_CHECK_COMPONENTS: ComponentName[] = [
  ComponentName.TRANSACTION,
  ComponentName.STRUCT,
  ComponentName.ENRICHMENT,
  ComponentName.CONNECTION,
  ComponentName.LIST,
  ComponentName.VELOCITY,
  ComponentName.LIST_VARIABLE,
  ComponentName.QUERY_VARIABLE,
  ComponentName.BASIC_VARIABLE,
  ComponentName.API_VARIABLE,
];

const NO_DEPLOYMENT_COMPONENTS: ComponentName[] = [ComponentName.STRUCT];
const ALWAYS_REMOVABLE_COMPONENTS: ComponentName[] = [ComponentName.ACH, ComponentName.CUSTOM_LIST];

class BulkActionUtil {
  /**
   * Analyzes selected items and returns their status distribution
   */
  public static analyzeSelectedItems(items: DeployableAccessBase[]): SelectedItemsStatus {
    let draftCount = 0;
    let activeCount = 0;

    for (const item of items) {
      if (item.stage === 'DRAFT') {
        draftCount++;
      } else {
        activeCount++;
      }
    }

    return {
      hasDraft: draftCount > 0,
      hasActive: activeCount > 0,
      hasMixed: draftCount > 0 && activeCount > 0,
      draftCount,
      activeCount,
    };
  }

  /**
   * Determines which bulk action buttons should be visible and their tooltips
   */
  public static getBulkActionVisibility(items: DeployableAccessBase[], component: ComponentName): BulkActionVisibility {
    const isAlwaysRemovableComponent = ALWAYS_REMOVABLE_COMPONENTS.includes(component);

    if (!items || items.length === 0) {
      return {
        showDeploy: false,
        showUndeploy: false,
        showRemove: isAlwaysRemovableComponent,
      };
    }

    if (isAlwaysRemovableComponent) {
      return {
        showDeploy: false,
        showUndeploy: false,
        showRemove: true,
      };
    }

    const status = this.analyzeSelectedItems(items);
    const isNoDeploymentComponent = NO_DEPLOYMENT_COMPONENTS.includes(component);

    if (isNoDeploymentComponent) {
      return {
        showDeploy: false,
        showUndeploy: false,
        showRemove: true,
      };
    }

    if (status.hasDraft && !status.hasActive) {
      return {
        showDeploy: true,
        showUndeploy: false,
        showRemove: true,
      };
    }

    if (status.hasActive && !status.hasDraft) {
      return {
        showDeploy: true,
        showUndeploy: true,
        showRemove: false,
      };
    }

    if (status.hasMixed) {
      return {
        showDeploy: true,
        showUndeploy: true,
        showRemove: true,
      };
    }

    return {
      showDeploy: false,
      showUndeploy: false,
      showRemove: false,
    };
  }

  /**
   * Filters items for deploy action (all items can be deployed/re-deployed)
   */
  public static getDeployableItems(items: DeployableAccessBase[]): DeployableAccessBase[] {
    return items;
  }

  /**
   * Filters items for undeploy action (only non-DRAFT items)
   */
  public static getUndeployableItems(items: DeployableAccessBase[]): DeployableAccessBase[] {
    return items.filter((item: DeployableAccessBase) => item.stage !== 'DRAFT');
  }

  /**
   * Filters items for remove action (only DRAFT items)
   */
  public static getRemovableItems(items: DeployableAccessBase[]): DeployableAccessBase[] {
    return items.filter((item: DeployableAccessBase) => item.stage === 'DRAFT');
  }

  /**
   * Checks if component requires dependency check before deletion
   */
  public static requiresDependencyCheck(component: ComponentName): boolean {
    return DEPENDENCY_CHECK_COMPONENTS.includes(component);
  }

  /**
   * Checks if component has deployment capability
   */
  public static hasDeployment(component: ComponentName): boolean {
    return !NO_DEPLOYMENT_COMPONENTS.includes(component);
  }
}

export default BulkActionUtil;
